'use client';
/* Built In Imports */
import { useState } from 'react';

/* External Imports */
import { Box, Heading, Image, Text } from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';

/* Internal Imports */
/* Components */
import ArtsCard from '@app-ps/Components/Card/ArtsCard';
import StructuredTextParser from '@app-ps/Utility/StructuredTextParser';
import config from '@config';

/* Services */
import { searchByFieldId } from '@components/Utility/Shared/SharedService';

/**
 * Renders ArtsAsDoorWays Component
 * @param {Object} sectionContent - Data from api
 * @param {Object} region - Data from api
 * @param {Object} lang - Data from api
 * @returns {ReactElement} - ArtsAsDoorWays Component
 */

const ArtsAsDoorWays = ({ sectionContent, region, lang }) => {
  const [art, setArt] = useState(sectionContent);

  const handleChange = () => {
    const updatedArt = art.map(oldArt => ({ ...oldArt, isActive: true }));
    setArt(updatedArt);
  };
  const samskritiData = sectionContent?.map(data => data);

  return (
    <Box
      w={'100%'}
      mt={{ base: '-.5rem', md: '2' }}
      mb={{ base: '15rem', sm: '2rem', md: '10rem', lg: '2' }}
      position="relative"
      pt={{ base: '0rem', sm: '10rem', md: '2rem', xl: '6rem', '2xl': '10rem' }}
      pb={{
        base: '0rem',
        md: '2rem',
        xl: '15rem',
        '2xl': '25rem',
      }}
      h="100%"
    >
      <Box maxW={'100%'} position="relative">
        <Box
          maxW={{ base: '100%', lg: '1170px', xl: '1420px' }}
          w="100%"
          textAlign={'left'}
          mt={{ base: '1.5rem', md: '0' }}
          mx="auto"
          px={{ base: '2', md: '2' }}
          pt={{ base: '1', md: '4' }}
          pb={{ base: '12rem', md: '4' }}
          color={'white'}
        >
          {/* Top Title Section */}
          <Box
            maxW={{ base: '100%', md: '60%', xl: '100%' }}
            w={{ base: '100%', md: '46.0rem' }}
            marginLeft={{ lg: '3rem', xl: '4.5rem', '2xl': '0' }}
            marginTop={{ base: '10rem', md: '0', lg: '3rem', xl: '0' }}
            mb={{ base: '3rem', '2xl': '3rem' }}
            p={{ base: 2, md: 4 }}
          >
            {sectionContent[0]?._modelApiKey === 'section_title' && (
              <>
                <Heading
                  fontSize={{
                    base: '1.75rem',
                    sm: '2.25rem',
                    lg: '2.65rem',
                    '2xl': '3.25rem',
                  }}
                  ml={{ base: '.5rem', md: '0' }}
                  fontFamily="var(--FedraSerifDisplay-Regular)"
                  color={'#FEFAF6'}
                  fontWeight="400"
                  lineHeight={{ base: '2.1rem', sm: '2.7rem', md: '2rem' }}
                  as="h2"
                >
                  {sectionContent[0]?.titleText}
                </Heading>
                <Heading
                  fontSize={{
                    base: '1.75rem',
                    sm: '2.25rem',
                    lg: '42px',
                    '2xl': '3.25rem',
                  }}
                  ml={{ base: '.5rem', md: '0' }}
                  fontWeight="700"
                  lineHeight={{ base: '2.1rem', sm: '2.7rem', md: '3.9rem' }}
                  whiteSpace={{ base: 'normal', sm: 'nowrap' }}
                  mb={{ base: '8', sm: '4', xl: '8' }}
                  as="h2"
                >
                  <Text
                    as={'span'}
                    mr={2}
                    color={'#FEFAF6'}
                    fontFamily="var(--FedraSerifDisplay-Bold)"
                  >
                    {sectionContent[0]?.additionalTextLine}
                  </Text>
                  <Text
                    color={'#FEFAF6'}
                    as="span"
                    fontFamily="var(--FedraSerifDisplay-Regular)"
                    fontWeight="normal"
                  >
                    {sectionContent[0]?.titleSubtext}
                  </Text>
                  <Text
                    as="span"
                    fontFamily="var(--FedraSerifDisplay-Bold)"
                    style={{ color: ' #60ADFF' }}
                  >
                    {sectionContent[1]?.text}
                  </Text>
                </Heading>
              </>
            )}
            {searchByFieldId(sectionContent, 'arts-text') && (
              <StructuredTextParser
                str={render(sectionContent[2]?.body)}
                fontFamily="var(--FedraSansStd-book)"
                fontSize={{
                  base: '0.875rem',
                  sm: '1rem',
                  xl: '18px',
                  '2xl': '24px',
                }}
                paddingRight={{ base: '2rem', md: '0', '2xl': '2.5rem' }}
                fontWeight="400"
                color="#FEFAF6"
                lineHeight={{
                  base: '1.4rem',
                  sm: '1.6rem',
                  md: '1.9rem',
                  xl: '28.8px',
                  '2xl': '38.4px',
                }}
                p={{ md: '0' }}
              />
            )}
          </Box>
          {/* Arts Card Section */}
          <Box
            display="flex"
            flexWrap={{ md: 'nowrap', base: 'wrap' }}
            alignItems="center"
            justifyContent="center"
            mx={'auto'}
            mt={4}
            mb={{
              base: '5rem',
              sm: '18rem',
              md: '8rem',
              lg: '8rem',
              xl: '-2rem',
            }}
            paddingX={{ lg: '3rem', '2xl': '0' }}
            gap={4}
            p={{ base: 0, md: 2 }}
          >
            {samskritiData?.map(card => (
              <ArtsCard
                key={card.id}
                cardList={card}
                handleHover={handleChange}
                handleRemove={() => setArt(sectionContent)}
                region={region}
                lang={lang}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Box position="absolute" top="0" zIndex="-1" w="100%" bottom="30%">
        <Box
          w="full"
          position="relative"
          height="100%"
          zIndex={{ base: 1, md: -1 }}
        >
          <Image
            src={`${config.imgPath}/d/46272/1723177848-artbg.png`}
            display={{ base: 'block', md: 'none' }}
            height={{ base: '690px', md: '70%' }}
            w="full"
          />
          <Image
            src="https://static.sadhguru.org/d/46272/1731929315-hero-bg.png"
            display={{ base: 'none', md: 'block' }}
            height={{ lg: '70%', xl: 'auto' }}
            w="full"
          />
        </Box>

        <Box
          position="absolute"
          bottom={{
            base: '-32rem',
            sm: '-35rem',
            md: '-18rem',
            lg: '-20rem',
            xl: '-23rem',
            '2xl': '-30rem',
          }}
          w="100%"
          top={{ base: '25%', md: '45%', lg: '52%' }}
          zIndex={0}
        >
          <Image
            src={`${config.imgPath}/d/46272/1718794715-group-131.png`}
            objectPosition="bottom"
            display={{ base: 'none', md: 'block' }}
            height="100%"
            objectFit="cover"
            w="full"
          />
          <Image
            src="https://static.sadhguru.org/d/46272/1731323846-artwhitebgphone.png"
            objectPosition="bottom"
            display={{ base: 'block', md: 'none' }}
            height="100%"
            objectFit="cover"
            w="full"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ArtsAsDoorWays;
